/**
 * useAgentInventory — encapsulates Agent Inventory (S12) UI state:
 *   tab (governed · shadow)
 *   view (table · grid)
 *   filters (search, department, vendor, status, risk, autonomy, drift)
 *   sort
 *
 * Returns the derived `governed`, `shadow`, and `filtered` datasets so the
 * page component stays a thin orchestrator (toolbar + panels).
 *
 * Pure logic; no UI. Mirrors the pattern used by `useOpportunityRadar`.
 */
import { useCallback, useMemo, useState } from "react";

const DEFAULT_FILTERS = {
  search: "",
  department: "",
  vendor: "",
  status: "",
  risk: "",
  autonomy: "",
  drift: "",
};

export function useAgentInventory(agents, sortOptions) {
  const [tab, setTab] = useState("governed");
  const [view, setView] = useState("table");
  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);
  const [sortId, setSortId] = useState(sortOptions[0]?.id ?? "");

  const setFilter = useCallback(
    (key) => (value) => setFiltersState((f) => ({ ...f, [key]: value })),
    [],
  );

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
    setSortId(sortOptions[0]?.id ?? "");
  }, [sortOptions]);

  const departments = useMemo(
    () => Array.from(new Set(agents.map((a) => a.department))).sort(),
    [agents],
  );
  const vendors = useMemo(
    () => Array.from(new Set(agents.map((a) => a.vendorName))).sort(),
    [agents],
  );

  const governed = useMemo(
    () => agents.filter((a) => a.agentType !== "shadow"),
    [agents],
  );
  const monitored = useMemo(
    () => agents.filter((a) => a.agentType !== "shadow" && a.status === "monitored"),
    [agents],
  );
  const shadow = useMemo(
    () => agents.filter((a) => a.agentType === "shadow"),
    [agents],
  );

  const dataset = tab === "shadow" ? shadow : tab === "monitored" ? monitored : governed;

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const sortCmp =
      sortOptions.find((s) => s.id === sortId)?.cmp || sortOptions[0]?.cmp;
    return dataset
      .filter((a) => matchesFilters(a, filters, tab, q))
      .sort(sortCmp);
  }, [dataset, filters, sortId, tab, sortOptions]);

  return {
    tab,
    setTab,
    view,
    setView,
    filters,
    setFilter,
    resetFilters,
    sortId,
    setSortId,
    departments,
    vendors,
    governed,
    monitored,
    shadow,
    dataset,
    filtered,
  };
}

function matchesFilters(a, f, tab, q) {
  if (q && !haystack(a).includes(q)) return false;
  if (f.department && a.department !== f.department) return false;
  if (f.vendor && a.vendorName !== f.vendor) return false;
  // Shadow tab keeps only the search + structural filters.
  if (tab === "shadow") return true;
  // Governed & Monitored tabs share the full filter set.
  if (f.status && a.status !== f.status) return false;
  if (f.risk && a.riskBand !== f.risk) return false;
  if (f.autonomy && a.autonomy !== f.autonomy) return false;
  if (f.drift && a.driftSignal !== f.drift) return false;
  return true;
}

function haystack(a) {
  return `${a.name} ${a.id} ${a.purpose} ${a.vendorName} ${a.ownerName}`.toLowerCase();
}
